import type { useStore } from "react-redux";
import type { useAppDispatch } from "@app/store";
import { type RootState } from "@app/store";
import { executeGraphqlRequest } from "@app/store/graphql-base-query";

// ─── Error types ──────────────────────────────────────────────────────────────

export class GqlError extends Error {
    readonly errorType: string | undefined;
    constructor(message: string, errorType?: string) {
        super(message);
        this.name = "GqlError";
        this.errorType = errorType;
    }
}

export const GROUP_CONCURRENCY_ERROR_TYPE = "GroupConcurrencyError";

/** Specific subclass for group concurrency conflicts; preserved for `instanceof` checks at call sites. */
export class GroupConcurrencyError extends GqlError {
    constructor(message: string) {
        super(message, GROUP_CONCURRENCY_ERROR_TYPE);
        this.name = GROUP_CONCURRENCY_ERROR_TYPE;
    }
}

/** Thrown when the user's token lacks the required permission for a query/mutation. */
export class ForbiddenError extends GqlError {
    readonly requiredPermission: string | undefined;
    constructor(message: string) {
        super(message, "Forbidden");
        this.name = "ForbiddenError";
        this.requiredPermission = message.match(/Missing required permission:\s*(.+)/i)?.[1]?.trim();
    }
}

/** Thrown when the token is malformed, expired, or structurally invalid. */
export class UnauthorizedError extends GqlError {
    constructor(message: string, errorType?: string) {
        super(message, errorType ?? "Unauthorized");
        this.name = "UnauthorizedError";
    }
}

// ─── Retry helper ─────────────────────────────────────────────────────────────

export function withGroupConcurrencyRetry<T>(fn: () => Promise<T>): Promise<T> {
    return fn().catch((err: unknown) => {
        if (err instanceof GqlError && err.errorType === GROUP_CONCURRENCY_ERROR_TYPE) return fn();
        throw err;
    });
}

// ─── Error classification ─────────────────────────────────────────────────────

const UNAUTHORIZED_ERROR_TYPES = new Set(["unauthorized", "tokenvalidationerror"]);

function classifyAndThrow(errorType: string | undefined, message: string): never {
    const normalized = errorType?.toLowerCase();

    if (normalized === GROUP_CONCURRENCY_ERROR_TYPE.toLowerCase()) {
        throw new GroupConcurrencyError(message);
    }
    if (normalized === "forbidden") {
        throw new ForbiddenError(message);
    }
    if (normalized && UNAUTHORIZED_ERROR_TYPES.has(normalized)) {
        throw new UnauthorizedError(message, errorType);
    }
    if (normalized === "aborterror") {
        throw new DOMException(message, "AbortError");
    }
    throw new GqlError(message, errorType);
}

// ─── Shared GQL execution helper ─────────────────────────────────────────────

export function makeGql(
    dispatch: ReturnType<typeof useAppDispatch>,
    store: ReturnType<typeof useStore<RootState>>,
    headers: () => Record<string, string>,
    endpoint: string,
) {
    return async function gql<T>(query: string, variables?: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
        const result = await executeGraphqlRequest(
            { document: query, variables, endpoint, headers: headers(), useDefaultApiKey: false },
            { getState: store.getState, dispatch, signal },
        );
        if ("error" in result) {
            const firstError = result.error?.errors?.[0];
            classifyAndThrow(firstError?.errorType, firstError?.message ?? "GraphQL error");
        }
        if (result.data === undefined) throw new Error("GraphQL returned no data");
        return result.data as T;
    };
}
